/**
 * Admin → Users → Usage: pure aggregation over the report from
 * GET /userAdmin/usage (azfApi/fastify/routes/auth/userUsage.js).
 *
 * The server sends the org's people plus compact activity rows:
 *   daily  [user, dayIndex, channel, events, sessions, reportType]
 *   hours  [user, dow (0 = Mon), hour, channel, activeDays]
 * Every filter (location, role, tag, usage type, device) is applied here so
 * switching one never refetches.
 *
 * Channels: desktop | mobile_web | mobile_app (the app) and email (a report
 * email open). "Active" = at least one row that passes the usage/device
 * filters. Statuses ignore those two filters — they describe the person:
 *   app      used the app in the period
 *   email    only opened report emails
 *   dormant  nothing in the period, but has signed in or been seen before
 *   never    never signed in and never seen
 */
import { ROLE_LABELS } from '../userModel';

export const PERIODS = [
  { value: 1, label: 'Today' },
  { value: 7, label: '7D' },
  { value: 30, label: '30D' },
  { value: 90, label: '90D' },
  { value: 365, label: '12M' },
];

export const USAGE_OPTIONS = [
  { value: 'all', label: 'All usage' },
  { value: 'app', label: 'App' },
  { value: 'email', label: 'Report emails' },
];

export const DEVICE_OPTIONS = [
  { value: 'all', label: 'All devices' },
  { value: 'desktop', label: 'Desktop' },
  { value: 'mobile', label: 'Mobile' },
];

export const SEGMENTS = [
  { value: 'all', label: 'All' },
  { value: 'app', label: 'Used the app' },
  { value: 'email', label: 'Email only' },
  { value: 'inactive', label: 'Not using' },
];

export const CHANNEL_LABELS = {
  desktop: 'Desktop web',
  mobile_app: 'Mobile app',
  mobile_web: 'Mobile web',
  email: 'Report email',
};

export const REPORT_LABELS = {
  'daily-performance': 'Daily Performance',
  'daily-sales-labor': 'Daily Sales + Labor',
  'ai-summary': 'AI Summary',
  other: 'Other',
};

export const STATUS_META = {
  app: { label: 'Active', tone: 'ok' },
  email: { label: 'Email only', tone: 'info' },
  dormant: { label: 'Dormant', tone: 'warn' },
  never: { label: 'Never signed in', tone: null },
};

export const DEFAULT_FILTERS = { locations: [], roles: [], tags: [], usage: 'all', device: 'all' };

// Data colours follow Settings → Appearance → Data colours: the --chart-*
// tokens (index.css) are remapped per palette on html[data-chart-palette],
// or set inline for a custom colour; Classic draws Lodge. Series pair the
// lead colour with neutrals that differ in lightness.
const SERIES = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-5)'];

export const SPLITS = {
  device: [
    { key: 'desktop', label: 'Desktop', color: 'var(--chart-1)' },
    { key: 'mobile', label: 'Mobile', color: 'var(--chart-3)' },
    { key: 'email', label: 'Email only', color: 'var(--chart-5)' },
  ],
  role: [
    { key: 'orgAdmin', label: ROLE_LABELS.orgAdmin, color: SERIES[0] },
    { key: 'orgViewer', label: ROLE_LABELS.orgViewer, color: SERIES[1] },
    { key: 'locationAdmin', label: ROLE_LABELS.locationAdmin, color: SERIES[2] },
    { key: 'locationViewer', label: ROLE_LABELS.locationViewer, color: SERIES[3] },
  ],
};

// Empty cell, then the palette's heat scale (--chart-scale-01 is near-white,
// indistinguishable from empty, so the ramp starts at 02).
export const HEAT_RAMP = [
  'var(--lodge-onyx-50)',
  'var(--chart-scale-02)',
  'var(--chart-scale-03)',
  'var(--chart-scale-04)',
  'var(--chart-scale-05)',
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DOW_LONG = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const isAppChannel = (c) => c === 'desktop' || c === 'mobile_app' || c === 'mobile_web';
const deviceOf = (c) => (c === 'desktop' ? 'desktop' : c === 'email' ? 'email' : 'mobile');

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

function ymdToDate(ymd) {
  return new Date(`${ymd}T00:00:00Z`);
}

export function dateAt(fromYmd, index) {
  const d = ymdToDate(fromYmd);
  d.setUTCDate(d.getUTCDate() + index);
  return d;
}

/** 0 = Monday … 6 = Sunday */
const mondayIndex = (d) => (d.getUTCDay() + 6) % 7;

export function formatDay(d, { weekday = false } = {}) {
  const base = `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
  return weekday ? `${DOW_SHORT[mondayIndex(d)]}, ${base}` : base;
}

export function hourLabel(h) {
  const x = ((h % 24) + 24) % 24;
  if (x === 0) return '12am';
  if (x < 12) return `${x}am`;
  if (x === 12) return '12pm';
  return `${x - 12}pm`;
}

export function hourRange(h) {
  return `${hourLabel(h)}–${hourLabel(h + 1)}`;
}

export const dowShort = (i) => DOW_SHORT[i];
export const dowLong = (i) => DOW_LONG[i];

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

export function rowPasses(channel, filters) {
  const f = filters || DEFAULT_FILTERS;
  if (f.usage === 'app' && !isAppChannel(channel)) return false;
  if (f.usage === 'email' && channel !== 'email') return false;
  if (f.device === 'desktop' && channel !== 'desktop') return false;
  if (f.device === 'mobile' && channel !== 'mobile_app' && channel !== 'mobile_web') return false;
  return true;
}

/** Indexes of the people the location / role / tag filters keep. */
export function scopeUsers(users, filters) {
  const f = filters || DEFAULT_FILTERS;
  const locs = new Set((f.locations || []).map(Number));
  const roles = new Set(f.roles || []);
  const tags = new Set(f.tags || []);
  const out = [];
  (users || []).forEach((u, i) => {
    if (locs.size && Array.isArray(u.locationIds) && !u.locationIds.some((id) => locs.has(Number(id)))) return;
    if (roles.size && !roles.has(u.role)) return;
    if (tags.size && !(u.tags || []).some((t) => tags.has(t))) return;
    out.push(i);
  });
  return out;
}

export function tagOptions(users) {
  const counts = new Map();
  (users || []).forEach((u) => (u.tags || []).forEach((t) => counts.set(t, (counts.get(t) || 0) + 1)));
  return [...counts.entries()]
    .sort((a, b) => a[0].localeCompare(b[0], undefined, { sensitivity: 'base' }))
    .map(([value, n]) => ({ value, label: value, hint: String(n) }));
}

// ---------------------------------------------------------------------------
// Per-person facts
// ---------------------------------------------------------------------------

/**
 * Per user: every channel's active days (unfiltered), the filtered active
 * days, report-email opens per type, and a status.
 */
export function personFacts(report, filters) {
  const users = report?.users || [];
  const facts = users.map(() => ({
    appDays: new Set(),
    emailDays: new Set(),
    anyDays: new Set(),
    filteredDays: new Set(),
    deviceDays: { desktop: new Set(), mobile: new Set(), email: new Set() },
    channels: new Set(),
    opens: 0,
    opensByReport: {},
    status: 'never',
  }));
  (report?.daily || []).forEach(([u, d, channel, events, , reportType]) => {
    const f = facts[u];
    if (!f) return;
    f.anyDays.add(d);
    f.channels.add(channel);
    f.deviceDays[deviceOf(channel)].add(d);
    if (channel === 'email') {
      f.emailDays.add(d);
      const n = Number(events) || 0;
      f.opens += n;
      const key = reportType || 'other';
      f.opensByReport[key] = (f.opensByReport[key] || 0) + n;
    } else {
      f.appDays.add(d);
    }
    if (rowPasses(channel, filters)) f.filteredDays.add(d);
  });
  facts.forEach((f, i) => {
    const u = users[i];
    if (f.appDays.size) f.status = 'app';
    else if (f.emailDays.size) f.status = 'email';
    else if (u.lastSignInAt || u.lastSeenAt) f.status = 'dormant';
    else f.status = 'never';
  });
  return facts;
}

const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);

/**
 * Everything the page's tiles, breakdowns and banner show, for the people in
 * scope under the current filters.
 */
export function summarize(report, filters) {
  const users = report?.users || [];
  const days = report?.period?.days || 0;
  const facts = personFacts(report, filters);
  const scope = scopeUsers(users, filters);
  const inScope = new Set(scope);

  const counts = { all: scope.length, app: 0, email: 0, dormant: 0, never: 0, inactive: 0 };
  let active = 0;
  let prevActive = 0;
  let opens = 0;
  let readers = 0;
  const reportTypes = new Set();
  scope.forEach((i) => {
    const f = facts[i];
    const u = users[i];
    counts[f.status] += 1;
    if (f.filteredDays.size) active += 1;
    const f2 = filters || DEFAULT_FILTERS;
    const prev = f2.usage === 'app' ? u.prevApp : f2.usage === 'email' ? u.prevEmail : (u.prevApp || u.prevEmail);
    if (prev) prevActive += 1;
    if (f.opens) readers += 1;
    opens += f.opens;
    Object.keys(f.opensByReport).forEach((k) => reportTypes.add(k));
  });
  counts.inactive = counts.dormant + counts.never;

  // average distinct people per day with a passing row
  const perDay = new Array(days).fill(0);
  const perDayApp = new Array(days).fill(0);
  scope.forEach((i) => {
    facts[i].filteredDays.forEach((d) => { if (d >= 0 && d < days) perDay[d] += 1; });
    facts[i].appDays.forEach((d) => { if (d >= 0 && d < days) perDayApp[d] += 1; });
  });
  const mean = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);
  const avgDaily = mean(perDay);
  const avgDailyApp = mean(perDayApp);

  const group = (keyOf, labelOf, keys) => {
    const map = new Map();
    (keys || []).forEach((k) => map.set(k, { key: k, label: labelOf(k), active: 0, total: 0 }));
    scope.forEach((i) => {
      [].concat(keyOf(users[i]) || []).forEach((k) => {
        if (!map.has(k)) map.set(k, { key: k, label: labelOf(k), active: 0, total: 0 });
        const row = map.get(k);
        row.total += 1;
        if (facts[i].filteredDays.size) row.active += 1;
      });
    });
    return [...map.values()].filter((r) => r.total > 0).map((r) => ({ ...r, pct: pct(r.active, r.total) }));
  };

  const byRole = group((u) => u.role || 'none', (k) => ROLE_LABELS[k] || 'No role', ['orgAdmin', 'orgViewer', 'locationAdmin', 'locationViewer']);
  const byTag = group((u) => u.tags || [], (k) => k).sort((a, b) => b.total - a.total || a.label.localeCompare(b.label));

  const appTypes = ['desktop', 'mobile_app', 'mobile_web'].map((c) => ({
    key: c,
    label: CHANNEL_LABELS[c],
    people: scope.filter((i) => facts[i].channels.has(c)).length,
  }));
  const emailMap = new Map();
  scope.forEach((i) => {
    Object.entries(facts[i].opensByReport).forEach(([k, n]) => {
      const row = emailMap.get(k) || { key: k, label: REPORT_LABELS[k] || k, opens: 0, readers: 0 };
      row.opens += n;
      row.readers += 1;
      emailMap.set(k, row);
    });
  });
  const emailTypes = [...emailMap.values()].sort((a, b) => b.opens - a.opens);

  return {
    facts,
    scope,
    inScope,
    counts,
    active,
    prevActive,
    // a device filter can't be applied to the previous period (the server
    // only keeps "used the app / an email" for it)
    showDelta: (filters || DEFAULT_FILTERS).device === 'all',
    avgDaily,
    avgDailyApp,
    stickiness: active ? avgDaily / active : 0,
    opens,
    readers,
    reportTypes: reportTypes.size,
    byRole,
    byTag,
    appTypes,
    emailTypes,
  };
}

// ---------------------------------------------------------------------------
// Trend
// ---------------------------------------------------------------------------

/** Which intervals make sense for a period, and the default. */
export function granularities(days) {
  if (days <= 1) return { options: ['hour'], fallback: 'hour' };
  if (days <= 7) return { options: ['day'], fallback: 'day' };
  if (days <= 30) return { options: ['day', 'week'], fallback: 'day' };
  if (days <= 90) return { options: ['day', 'week'], fallback: 'week' };
  return { options: ['week', 'month'], fallback: 'month' };
}

function buckets(report, gran) {
  const days = report?.period?.days || 0;
  const from = report?.period?.from;
  const out = [];
  if (!from || !days) return { list: out, of: () => -1 };
  const indexOfDay = new Array(days);
  for (let i = 0; i < days; i += 1) {
    const d = dateAt(from, i);
    let key;
    let label;
    let short;
    if (gran === 'week') {
      const start = dateAt(from, i - mondayIndex(d));
      key = start.toISOString().slice(0, 10);
      label = `Week of ${formatDay(start)}`;
      short = formatDay(start);
    } else if (gran === 'month') {
      key = `${d.getUTCFullYear()}-${d.getUTCMonth()}`;
      label = `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
      short = MONTHS[d.getUTCMonth()];
    } else {
      key = String(i);
      label = formatDay(d, { weekday: true });
      short = mondayIndex(d) === 0 || days <= 7 ? formatDay(d) : '';
    }
    let last = out[out.length - 1];
    if (!last || last.key !== key) {
      last = { key, label, short, firstDay: i, lastDay: i };
      out.push(last);
    }
    last.lastDay = i;
    indexOfDay[i] = out.length - 1;
  }
  // a partial first/last bucket says so
  if (gran !== 'day' && out.length) {
    const first = out[0];
    const lastB = out[out.length - 1];
    const span = (b) => b.lastDay - b.firstDay + 1;
    if (gran === 'week') {
      if (span(first) < 7) first.label += ' (partial)';
      if (out.length > 1 && span(lastB) < 7) lastB.label += ' (to date)';
    } else {
      if (dateAt(from, first.firstDay).getUTCDate() !== 1) first.label += ' (partial)';
      if (out.length > 1) lastB.label += ' (to date)';
    }
  }
  return { list: out, of: (d) => (d >= 0 && d < days ? indexOfDay[d] : -1) };
}

/**
 * Unique active people per bucket, split by device (each person counted
 * once, under the first of desktop → mobile → email they used that bucket)
 * or by role.
 */
export function trend(report, filters, gran, split) {
  if (gran === 'hour') return hourlyTrend(report, filters, split);
  const users = report?.users || [];
  const series = SPLITS[split] || SPLITS.device;
  const { list, of } = buckets(report, gran);
  const inScope = new Set(scopeUsers(users, filters));
  // per bucket: user → best device rank
  const seen = list.map(() => new Map());
  const rank = { desktop: 0, mobile: 1, email: 2 };
  (report?.daily || []).forEach(([u, d, channel]) => {
    if (!inScope.has(u) || !rowPasses(channel, filters)) return;
    const b = of(d);
    if (b < 0) return;
    const r = rank[deviceOf(channel)];
    const cur = seen[b].get(u);
    if (cur === undefined || r < cur) seen[b].set(u, r);
  });
  const out = list.map((b, bi) => {
    const values = series.map(() => 0);
    seen[bi].forEach((r, u) => {
      const k = split === 'role'
        ? series.findIndex((s) => s.key === users[u].role)
        : r;
      if (k >= 0) values[k] += 1;
    });
    const total = seen[bi].size;
    return { key: b.key, label: b.label, short: b.short, firstDay: b.firstDay, lastDay: b.lastDay, total, values };
  });
  return { series, buckets: out };
}

/** Today, hour by hour (from the period's hour rows; a one-day period has one weekday). */
function hourlyTrend(report, filters, split) {
  const users = report?.users || [];
  const series = SPLITS[split] || SPLITS.device;
  const inScope = new Set(scopeUsers(users, filters));
  const rank = { desktop: 0, mobile: 1, email: 2 };
  const seen = Array.from({ length: 24 }, () => new Map());
  let dow = null;
  (report?.hours || []).forEach(([u, d, hour, channel, n]) => {
    if (!(Number(n) > 0) || !inScope.has(u) || !rowPasses(channel, filters)) return;
    dow = d;
    const r = rank[deviceOf(channel)];
    const cur = seen[hour].get(u);
    if (cur === undefined || r < cur) seen[hour].set(u, r);
  });
  if (dow === null && report?.period?.from) dow = mondayIndex(dateAt(report.period.from, 0));
  const buckets = seen.map((m, hour) => {
    const values = series.map(() => 0);
    m.forEach((r, u) => {
      const k = split === 'role' ? series.findIndex((x) => x.key === users[u].role) : r;
      if (k >= 0) values[k] += 1;
    });
    return { key: String(hour), label: hourRange(hour), short: hour % 3 === 0 ? hourLabel(hour) : '', hour, dow, total: m.size, values };
  });
  return { series, buckets };
}

/** People in scope whose last activity is within `minutes` of `now`. */
export function activeNow(report, filters, minutes = 15, now = Date.now()) {
  const users = report?.users || [];
  return scopeUsers(users, filters).filter((i) => {
    const t = users[i].lastSeenAt ? Date.parse(users[i].lastSeenAt) : NaN;
    return Number.isFinite(t) && now - t <= minutes * 60000;
  }).length;
}

/** Round the axis up to 4 even steps. */
export function axis(max) {
  const steps = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 5000];
  const step = steps.find((s) => s * 4 >= max) || Math.ceil(max / 4);
  return { top: step * 4, ticks: [4, 3, 2, 1, 0].map((i) => step * i) };
}

// ---------------------------------------------------------------------------
// Heatmap
// ---------------------------------------------------------------------------

/**
 * Average active people per hour of the week: for each person the most
 * active days in that slot across the channels the filters keep, summed,
 * then divided by how many of that weekday the period has.
 */
export function heatmap(report, filters) {
  const users = report?.users || [];
  const days = report?.period?.days || 0;
  const from = report?.period?.from;
  const inScope = new Set(scopeUsers(users, filters));
  const weekdays = new Array(7).fill(0);
  for (let i = 0; i < days && from; i += 1) weekdays[mondayIndex(dateAt(from, i))] += 1;

  const best = new Map(); // `${u}:${dow}:${h}` → days
  (report?.hours || []).forEach(([u, dow, hour, channel, n]) => {
    if (!inScope.has(u) || !rowPasses(channel, filters)) return;
    const key = `${u}:${dow}:${hour}`;
    best.set(key, Math.max(best.get(key) || 0, Number(n) || 0));
  });
  const grid = Array.from({ length: 7 }, () => new Array(24).fill(0));
  best.forEach((n, key) => {
    const [, dow, hour] = key.split(':').map(Number);
    grid[dow][hour] += n;
  });
  let max = 0;
  let peak = null;
  grid.forEach((row, dow) => row.forEach((sum, hour) => {
    const v = weekdays[dow] ? sum / weekdays[dow] : 0;
    grid[dow][hour] = v;
    if (v > max) { max = v; peak = { dow, hour, value: v }; }
  }));
  return { grid, max, peak };
}

const CHANNEL_BITS = { desktop: 1, mobile_app: 2, mobile_web: 4, email: 8 };
const maskChannels = (mask) => Object.keys(CHANNEL_BITS).filter((c) => mask & CHANNEL_BITS[c]);
const maskPasses = (mask, filters) => maskChannels(mask).some((c) => rowPasses(c, filters));

/**
 * Contribution-style calendar of the last 53 weeks (report.calendar, not the
 * selected period): one column per week, Mon–Sun rows, each date shaded by
 * the distinct people active that day (usage/device filters applied). Also
 * the average per weekday.
 */
export function calendarHeat(report, filters) {
  const users = report?.users || [];
  const cal = report?.calendar || {};
  const days = cal.days || 0;
  const from = cal.from;
  const inScope = new Set(scopeUsers(users, filters));
  const people = Array.from({ length: days }, () => new Set());
  (cal.rows || []).forEach(([u, d, mask]) => {
    if (d < 0 || d >= days || !inScope.has(u) || !maskPasses(mask, filters)) return;
    people[d].add(u);
  });

  const weeks = [];
  let max = 0;
  let peak = null;
  let total = 0;
  const weekdaySum = new Array(7).fill(0);
  const weekdayN = new Array(7).fill(0);
  if (from && days) {
    const lead = mondayIndex(dateAt(from, 0));
    for (let start = -lead; start < days; start += 7) {
      const cells = [];
      let monthLabel = '';
      for (let dow = 0; dow < 7; dow += 1) {
        const i = start + dow;
        if (i < 0 || i >= days) { cells.push(null); continue; }
        const date = dateAt(from, i);
        const value = people[i].size;
        // the first column, and the column holding each month's 1st, names the month
        if (!weeks.length && !monthLabel) monthLabel = MONTHS[date.getUTCMonth()];
        if (date.getUTCDate() === 1) monthLabel = MONTHS[date.getUTCMonth()];
        weekdaySum[dow] += value;
        weekdayN[dow] += 1;
        total += value;
        if (value > max) max = value;
        const label = `${formatDay(date, { weekday: true })}, ${date.getUTCFullYear()}`;
        if (value > 0 && (!peak || value > peak.value)) peak = { index: i, value, label };
        cells.push({ index: i, label, value });
      }
      weeks.push({ key: String(start), monthLabel, cells });
    }
  }
  // a first-column label right before the next month's label would collide
  if (weeks.length > 1 && weeks[0].monthLabel && weeks[1].monthLabel) weeks[0].monthLabel = '';
  const weekdayAvg = weekdaySum.map((t, i) => (weekdayN[i] ? t / weekdayN[i] : 0));
  const uniques = new Set();
  people.forEach((set) => set.forEach((u) => uniques.add(u)));
  return { weeks, max, peak, total, people: uniques.size, weekdayAvg, weekdayMax: Math.max(0, ...weekdayAvg) };
}

/** Calendar day indexes on a weekday (0 = Mon). */
export function calendarWeekdayIndexes(report, dow) {
  const cal = report?.calendar || {};
  const out = [];
  for (let i = 0; i < (cal.days || 0) && cal.from; i += 1) if (mondayIndex(dateAt(cal.from, i)) === dow) out.push(i);
  return out;
}

export function heatLevel(value, max) {
  if (!(value > 0) || !(max > 0)) return 0;
  return Math.min(HEAT_RAMP.length - 1, 1 + Math.floor((value / max) * (HEAT_RAMP.length - 1 - 1e-9)));
}

// ---------------------------------------------------------------------------
// People table
// ---------------------------------------------------------------------------

/** Last `n` days of the period as 'app' | 'email' | 'none'. */
export function activityStrip(fact, periodDays, n = 30) {
  const len = Math.min(n, periodDays);
  const out = [];
  for (let d = periodDays - len; d < periodDays; d += 1) {
    out.push(fact.appDays.has(d) ? 'app' : fact.emailDays.has(d) ? 'email' : 'none');
  }
  return out;
}

/** Share of active days by desktop / mobile / email (a day counts once per device). */
export function deviceMix(fact) {
  const d = fact.deviceDays.desktop.size;
  const m = fact.deviceDays.mobile.size;
  const e = fact.deviceDays.email.size;
  const total = d + m + e;
  if (!total) return null;
  return { desktop: pct(d, total), mobile: pct(m, total), email: Math.max(0, 100 - pct(d, total) - pct(m, total)) };
}

export function mixLabel(mix) {
  if (!mix) return '';
  const top = [['desktop', mix.desktop], ['mobile', mix.mobile], ['email', mix.email]].sort((a, b) => b[1] - a[1])[0];
  return `${top[1]}% ${top[0]}`;
}

const STATUS_ORDER = { app: 0, email: 1, dormant: 2, never: 3 };

/** Table rows for a segment + search, most recently active first. */
export function peopleRows(report, summary, { segment = 'all', search = '' } = {}) {
  const users = report?.users || [];
  const days = report?.period?.days || 0;
  const q = search.trim().toLowerCase();
  return summary.scope
    .map((i) => ({ i, u: users[i], f: summary.facts[i] }))
    .filter(({ f }) => (segment === 'all' ? true : segment === 'inactive' ? f.status === 'dormant' || f.status === 'never' : f.status === segment))
    .filter(({ u }) => !q || [u.name, u.email, u.jobTitle, ...(u.tags || [])].some((v) => String(v || '').toLowerCase().includes(q)))
    .map(({ i, u, f }) => ({
      index: i,
      user: u,
      status: f.status,
      activeDays: f.appDays.size,
      emailDays: f.emailDays.size,
      opens: f.opens,
      strip: activityStrip(f, days),
      mix: deviceMix(f),
      lastAt: u.lastSeenAt || null,
    }))
    .sort((a, b) => {
      const ta = a.lastAt ? Date.parse(a.lastAt) : 0;
      const tb = b.lastAt ? Date.parse(b.lastAt) : 0;
      if (ta !== tb) return tb - ta;
      return STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
        || String(a.user.name).localeCompare(String(b.user.name), undefined, { sensitivity: 'base' });
    });
}

/** Flat rows for the CSV/XLSX export. */
export function exportRows(rows, report) {
  return rows.map((r) => ({
    Name: r.user.name || '',
    Email: r.user.email || '',
    'Job Title': r.user.jobTitle || '',
    Role: ROLE_LABELS[r.user.role] || r.user.role || '',
    Tags: (r.user.tags || []).join(', '),
    Status: STATUS_META[r.status]?.label || r.status,
    'Last Active': r.lastAt || '',
    'Last Active Via': CHANNEL_LABELS[r.user.lastSeenChannel] || '',
    [`App Days (${report?.period?.days || 0}d)`]: r.activeDays,
    'Email Open Days': r.emailDays,
    'Report Emails Opened': r.opens,
    'Desktop %': r.mix ? r.mix.desktop : '',
    'Mobile %': r.mix ? r.mix.mobile : '',
    'Email %': r.mix ? r.mix.email : '',
    'Last Sign-in': r.user.lastSignInAt || '',
    Invited: r.user.invitedAt || '',
  }));
}

/** Detail panel: 13 weeks (Mon–Sun columns) of 'app' | 'email' | 'none' | null (future/outside). */
export function calendarWeeks(detail) {
  const cal = detail?.calendar;
  if (!cal?.from || !cal?.days) return [];
  const kind = new Array(cal.days).fill('none');
  (detail.daily || []).forEach(([d, channel]) => {
    if (d < 0 || d >= cal.days) return;
    if (channel !== 'email') kind[d] = 'app';
    else if (kind[d] === 'none') kind[d] = 'email';
  });
  const lead = mondayIndex(dateAt(cal.from, 0));
  const cells = [...new Array(lead).fill(null), ...kind];
  while (cells.length % 7) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/** Detail panel: hour-of-week grid of active days (max across channels). */
export function detailHeat(detail) {
  const grid = Array.from({ length: 7 }, () => new Array(24).fill(0));
  const best = new Map();
  (detail?.hours || []).forEach(([dow, hour, , n]) => {
    const key = `${dow}:${hour}`;
    best.set(key, Math.max(best.get(key) || 0, Number(n) || 0));
  });
  let max = 0;
  best.forEach((n, key) => {
    const [dow, hour] = key.split(':').map(Number);
    if (grid[dow]) grid[dow][hour] = n;
    if (n > max) max = n;
  });
  return { grid, max };
}

/** Detail panel: share of active days per channel over the calendar. */
export function detailDevices(detail) {
  const byChannel = { mobile_app: new Set(), desktop: new Set(), mobile_web: new Set(), email: new Set() };
  (detail?.daily || []).forEach(([d, channel]) => { if (byChannel[channel]) byChannel[channel].add(d); });
  const total = Object.values(byChannel).reduce((a, s) => a + s.size, 0);
  return Object.entries(byChannel)
    .map(([key, s]) => ({ key, label: CHANNEL_LABELS[key], days: s.size, pct: pct(s.size, total) }))
    .filter((r) => r.days > 0)
    .sort((a, b) => b.days - a.days);
}

export function minuteLabel(minute) {
  if (minute === null || minute === undefined) return null;
  const h = Math.floor(minute / 60);
  const m = minute % 60;
  const ampm = h < 12 ? 'am' : 'pm';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')}${ampm}`;
}

// ---------------------------------------------------------------------------
// Drill-in: who is behind a number
// ---------------------------------------------------------------------------

/** Day indexes of the period that fall on a weekday (0 = Mon). */
export function weekdayIndexes(report, dow) {
  const days = report?.period?.days || 0;
  const from = report?.period?.from;
  const out = [];
  for (let i = 0; i < days && from; i += 1) if (mondayIndex(dateAt(from, i)) === dow) out.push(i);
  return out;
}

/**
 * The people behind a selection, most active first. Selections:
 *   { kind: 'days', days: [dayIndex…] }        a date, a bar, a weekday
 *   { kind: 'hour', dow, hour }                 a time-of-day cell
 *   { kind: 'role' | 'tag', key }               a breakdown row
 *   { kind: 'channel', channel }                an app device row
 *   { kind: 'report', report }                  a report-email row
 * People/usage/device filters apply, except a channel or report selection
 * names its own rows. `days` counts active days inside the selection.
 */
export function drillPeople(report, filters, sel) {
  const users = report?.users || [];
  const scope = scopeUsers(users, filters);
  const inScope = new Set(scope);
  const acc = new Map();
  const get = (u) => {
    if (!acc.has(u)) {
      acc.set(u, { index: u, user: users[u], days: new Set(), slotDays: 0, events: 0, sessions: 0, opens: 0, devices: new Set() });
    }
    return acc.get(u);
  };
  const take = (u, d, channel, events, sessions) => {
    const a = get(u);
    a.days.add(d);
    a.devices.add(deviceOf(channel));
    if (channel === 'email') a.opens += Number(events) || 0;
    else { a.events += Number(events) || 0; a.sessions += Number(sessions) || 0; }
  };

  if (sel?.kind === 'calendar') {
    const daySet = new Set(sel.days || []);
    (report?.calendar?.rows || []).forEach(([u, d, mask]) => {
      if (!daySet.has(d) || !inScope.has(u) || !maskPasses(mask, filters)) return;
      const a = get(u);
      a.days.add(d);
      maskChannels(mask).forEach((c) => a.devices.add(deviceOf(c)));
    });
  } else if (sel?.kind === 'hour') {
    (report?.hours || []).forEach(([u, dow, hour, channel, n]) => {
      if (dow !== sel.dow || hour !== sel.hour || !inScope.has(u) || !rowPasses(channel, filters)) return;
      const a = get(u);
      a.slotDays = Math.max(a.slotDays, Number(n) || 0);
      a.devices.add(deviceOf(channel));
    });
  } else {
    const daySet = sel?.kind === 'days' ? new Set(sel.days || []) : null;
    (report?.daily || []).forEach(([u, d, channel, events, sessions, reportType]) => {
      if (!inScope.has(u)) return;
      const user = users[u];
      if (sel?.kind === 'days' && (!daySet.has(d) || !rowPasses(channel, filters))) return;
      if (sel?.kind === 'role' && (user.role !== sel.key || !rowPasses(channel, filters))) return;
      if (sel?.kind === 'tag' && (!(user.tags || []).includes(sel.key) || !rowPasses(channel, filters))) return;
      if (sel?.kind === 'channel' && channel !== sel.channel) return;
      if (sel?.kind === 'report' && (channel !== 'email' || (reportType || 'other') !== sel.report)) return;
      take(u, d, channel, events, sessions);
    });
  }

  const rows = [...acc.values()].map((a) => ({
    index: a.index,
    user: a.user,
    days: sel?.kind === 'hour' ? a.slotDays : a.days.size,
    events: a.events,
    sessions: a.sessions,
    opens: a.opens,
    devices: ['desktop', 'mobile', 'email'].filter((d) => a.devices.has(d)),
  }));
  rows.sort((a, b) => b.days - a.days || (b.events + b.opens) - (a.events + a.opens)
    || String(a.user.name).localeCompare(String(b.user.name), undefined, { sensitivity: 'base' }));

  // Group selections also list who in the group was not active.
  let inactive = [];
  if (sel?.kind === 'role' || sel?.kind === 'tag') {
    inactive = scope
      .filter((i) => !acc.has(i))
      .filter((i) => (sel.kind === 'role' ? users[i].role === sel.key : (users[i].tags || []).includes(sel.key)))
      .map((i) => users[i]);
  }
  return { rows, inactive };
}
